// Education.jsx
import React, { useState } from 'react';
import './Education.css';

const Education = () => {
  const [selectedCert, setSelectedCert] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const educationData = [
    {
      id: 1,
      degree: "CFA Program (USA) Level 1",
      institution: "CFA Institute",
      year: "August 2022",
      status: "Pass",
      icon: "🎓",
      highlight: true
    },
    {
      id: 2,
      degree: "M.Com",
      institution: "C.C.S University",
      year: "2002",
      percentage: "First Class",
      icon: "📊",
      highlight: false
    },
    {
      id: 3,
      degree: "PGDBM Finance - DLP",
      institution: "IMT Ghaziabad",
      year: "2004",
      percentage: "First Class",
      icon: "💼",
      highlight: false
    },
    {
      id: 4,
      degree: "CA - Intermediate",
      institution: "The Institute of Chartered Accountants of India",
      year: "November 2002",
      status: "Pass",
      icon: "📈",
      highlight: false
    },
    {
      id: 5,
      degree: "B.Sc. (Physics, Chemistry, Mathematics)",
      institution: "C.C.S University",
      year: "1996",
      percentage: "First Class",
      icon: "🔬",
      highlight: false
    }
  ];

  const certifications = [
    {
      id: 1,
      name: "NSC Certified Market Professional",
      issuer: "NSC",
      date: "July 01, 2010",
      icon: "🧠",
      image: "/assets/certificates/certificate1.png"
    },
    {
      id: 2,
      name: "Online Dispute Resolution (ODR)",
      issuer: "National Institute of Securities Markets",
      date: "March 27, 2025",
      icon: "📚",
      image: "assets/certificates/certificate2.png"
    },
    {
      id: 3,
      name: "Applied Data Science and AI",
      issuer: "upGrad",
      date: "Sept-Oct 2024",
      icon: "💹",
      image: "assets/certificates/certificate3.png"
    },
    {
      id: 4,
      name: "Behavioral Finance",
      issuer: "Duke University",
      date: "July 28, 2024",
      icon: "⚖️",
      image: "assets/certificates/certificate4.png"
    },
    {
      id: 5,
      name: "Equity Indexes, Valuations and Investment Vehicles",
      issuer: "NewYork Institute of Finance",
      date: "June 30, 2024",
      icon: "📊",
      image: "assets/certificates/certificate5.png"
    },
    {
      id: 6,
      name: "Financial Analysis for Decision Making",
      issuer: "Barbson University",
      date: "June 29, 2024",
      icon: "🏛️",
      image: "assets/certificates/certificate6.png"
    },
    {
      id: 7,
      name: "Introduction to Corporate Finance",
      issuer: "ColumbiaX",
      date: "June 29, 2024",
      icon: "🏛️",
      image: "assets/certificates/certificate7.png"
    },
    {
      id: 8,
      name: "Financial Markets and Products",
      issuer: "SBI",
      date: "June 29, 2024",
      icon: "🏛️",
      image: "assets/certificates/certificate8.png"
    },
    {
      id: 9,
      name: "Employment Generation among Rural Youth",
      issuer: "AGMOOCS",
      date: "June 29, 2024",
      icon: "🏛️",
      image: "assets/certificates/certificate9.png"
    }
  ];

  // ----------- Sorting Logic (Reverse Chronological: New → Old) -----------

  // Extract last numeric year from mixed year formats
  const parseYear = (value) => {
    const cleaned = value.replace(/[^0-9]/g, " ").trim().split(" ");
    return parseInt(cleaned[cleaned.length - 1]);
  };

  // Sort education newest → oldest
  const sortedEducation = [...educationData].sort((a, b) => {
    return parseYear(b.year) - parseYear(a.year);
  });

  // Sort certifications newest → oldest
  const sortedCerts = [...certifications].sort((a) => {
    return parseYear(a.date) ;
  });

  // -------- Modal --------
  const openCertificateModal = (cert) => {
    setSelectedCert(cert);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeCertificateModal = () => {
    setIsModalOpen(false);
    setSelectedCert(null);
    document.body.style.overflow = 'unset';
  };

  return (
    <>
      <section className="education" id="education">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Education & Certifications</h2>
            <p className="section-subtitle">
              A strong academic foundation complemented by continuous professional development 
              in finance and emerging technologies.
            </p>
          </div>

          <div className="education-content">

            {/* -------- Academic Timeline -------- */}
            <div className="education-section">
              <h3 className="section-heading">
                <span className="heading-icon">🎓</span>
                Academic Qualifications
              </h3>

              <div className="timeline">
                {sortedEducation.map((edu, index) => (
                  <div 
                    key={edu.id} 
                    className={`timeline-item ${edu.highlight ? 'highlight' : ''}`}
                    data-aos="fade-up"
                    data-aos-delay={index * 100}
                  >
                    <div className="timeline-marker">
                      <span className="marker-icon">{edu.icon}</span>
                      <div className="marker-line"></div>
                    </div>

                    <div className="timeline-content">
                      <div className="degree-info">
                        <h4 className="degree">{edu.degree}</h4>
                        <p className="institution">{edu.institution}</p>

                        <div className="details">
                          <span className="year">{edu.year}</span>
                          {edu.percentage && (
                            <span className="percentage">• {edu.percentage}</span>
                          )}
                          {edu.status && (
                            <span className="status">• {edu.status}</span>
                          )}
                        </div>
                      </div>

                      {edu.highlight && (
                        <div className="highlight-badge">
                          <span>Latest Achievement</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* -------- Certifications Section -------- */}
            <div className="certifications-section">
              <h3 className="section-heading">
                <span className="heading-icon">🏆</span>
                Professional Certifications
              </h3>

              <div className="certifications-grid">
                {sortedCerts.map((cert, index) => (
                  <div 
                    key={cert.id} 
                    className="certification-card"
                    data-aos="zoom-in"
                    data-aos-delay={index * 50}
                  >
                    <div className="cert-icon">{cert.icon}</div>

                    <div className="cert-content">
                      <h4 className="cert-name">{cert.name}</h4>
                      <p className="cert-issuer">{cert.issuer}</p>
                      {/* <span className="cert-date">{cert.date}</span> */}
                    </div>

                    <button 
                      className="cert-view-btn"
                      onClick={() => openCertificateModal(cert)}
                    >
                      View Certificate
                    </button>

                    <div className="cert-ribbon"></div>
                  </div>
                ))}
              </div>

              <div className="additional-certs">
                <p>
                  <strong>Plus:</strong> NISM Research Analyst Certification, NCMP Level 2, 
                  and multiple NCFM modules with outstanding scores (87-99%)
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* -------- Certificate Modal -------- */}
      {isModalOpen && selectedCert && (
        <div className="certificate-modal-overlay" onClick={closeCertificateModal}>
          <div className="certificate-modal" onClick={(e) => e.stopPropagation()}>

            <button className="modal-close-btn" onClick={closeCertificateModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" 
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>

            <div className="modal-header">
              <h3 className="modal-title">{selectedCert.name}</h3>
              <p className="modal-issuer">{selectedCert.issuer}</p>
              {/* <span className="modal-date">{selectedCert.date}</span> */}
            </div>

            <div className="certificate-image-container">
              <img 
                src={selectedCert.image} 
                alt={`Certificate for ${selectedCert.name}`}
                className="certificate-image"
                onError={(e) => {
                  e.target.src = '/certificates/placeholder.jpg';
                  e.target.alt = 'Certificate image not available';
                }}
              />
            </div>

            <div className="modal-actions">
              <button className="btn-close" onClick={closeCertificateModal}>
                Close
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default Education;
